(() => {
  'use strict';

  const CAMPAIGN_ID = 'build-your-calm-kit-v1';
  const ELIGIBLE_PRODUCT_IDS = new Set([
    10443029971249,
    10703070003505,
    10656798474545,
    10689255047473,
    8901235966257,
    10444269945137,
    8902125093169,
    10661074862385,
    10336302891313,
    10269204054321,
    10744806703409,
    10744806768945,
  ]);
  const GIFT_RULES = new Map([
    ['workbook', {variantId: 51724047384881, unlockQuantity: 2}],
    ['stickers', {variantId: 51943608877361, unlockQuantity: 2}],
    ['tote', {variantId: 47446837985585, unlockQuantity: 3}],
    ['golden-ticket', {variantId: 51943498055985, unlockQuantity: 4}],
  ]);
  const GROUNDING_CAMPAIGN_ID = 'grounding-kids-lp-v1';
  const GROUNDING_KIDS_PRODUCT_ID = 10444269945137;
  const GROUNDING_ADULT_PRODUCT_ID = 10443029971249;
  const GROUNDING_GIFT_RULES = new Map([
    ['workbook', {variantId: 51724047384881, unlockQuantity: 1}],
    ['stickers', {variantId: 51943608877361, unlockQuantity: 1}],
    ['tote', {variantId: 47446837985585, unlockQuantity: 2}],
    ['golden-ticket', {variantId: 51943498055985, unlockQuantity: 3}],
  ]);
  const root = window.Shopify?.routes?.root || '/';
  const originalFetch = window.fetch.bind(window);
  let timer;
  let reconciling = false;
  let reconciliationPending = false;

  function schedule(delay = 350) {
    window.clearTimeout(timer);
    timer = window.setTimeout(() => {
      // Preserve mutations that land while a reconciliation request is active.
      if (reconciling) {
        reconciliationPending = true;
        return;
      }
      reconcile();
    }, delay);
  }

  function calmProperties(item) {
    const properties = item?.properties || {};
    if (properties._calm_kit_campaign !== CAMPAIGN_ID || !properties._calm_kit_instance) return null;
    return properties;
  }

  function calmReconciliationPlan(cart) {
    const instances = new Map();
    const isOfferActive = window.CalmKitCommerceEnabled === true;

    for (const item of cart.items || []) {
      const properties = calmProperties(item);
      if (!properties) continue;
      const instanceId = properties._calm_kit_instance;
      const instance = instances.get(instanceId) || {id: instanceId, qualifyingQuantity: 0, gifts: []};
      instances.set(instanceId, instance);

      if (properties._calm_kit_role === 'qualifying' && ELIGIBLE_PRODUCT_IDS.has(Number(item.product_id))) {
        instance.qualifyingQuantity += Number(item.quantity) || 0;
      } else if (properties._calm_kit_role === 'gift') {
        instance.gifts.push({item, properties});
      }
    }

    const updates = {};
    const additions = [];
    for (const instance of instances.values()) {
      const seenGiftKeys = new Set();
      for (const {item, properties} of instance.gifts) {
        const key = properties._calm_kit_gift_key;
        const rule = GIFT_RULES.get(key);
        const valid = isOfferActive &&
          properties._calm_kit_gift === '1' &&
          rule &&
          Number(item.variant_id) === rule.variantId &&
          Number(item.quantity) === 1 &&
          instance.qualifyingQuantity >= rule.unlockQuantity &&
          instance.qualifyingQuantity <= 4 &&
          !seenGiftKeys.has(key);

        if (valid) seenGiftKeys.add(key);
        else updates[item.key] = 0;
      }

      if (!isOfferActive || instance.qualifyingQuantity < 2 || instance.qualifyingQuantity > 4) continue;
      for (const [key, rule] of GIFT_RULES) {
        if (rule.unlockQuantity > instance.qualifyingQuantity || seenGiftKeys.has(key)) continue;
        additions.push({
          id: rule.variantId,
          quantity: 1,
          properties: {
            _calm_kit_campaign: CAMPAIGN_ID,
            _calm_kit_instance: instance.id,
            _calm_kit_line: `gift-${key}-${instance.id}`,
            _calm_kit_role: 'gift',
            _calm_kit_gift: '1',
            _calm_kit_gift_key: key,
          },
        });
      }
    }
    return {updates, additions};
  }

  function groundingProperties(item) {
    const properties = item?.properties || {};
    if (
      properties._landing_offer_campaign !== GROUNDING_CAMPAIGN_ID ||
      !properties._landing_offer_instance
    ) return null;
    return properties;
  }

  function validGroundingComposition(instance) {
    if (instance.invalid) return false;
    if (instance.pack === 'single') return instance.kids === 1 && instance.adults === 0;
    if (instance.pack === 'siblings') return instance.kids === 2 && instance.adults === 0;
    if (instance.pack === 'three') return instance.kids === 3 && instance.adults === 0;
    if (instance.pack === 'momkid') return instance.kids === 1 && instance.adults === 1;
    // Every unknown browser-supplied pack key fails closed.
    return false;
  }

  function groundingReconciliationPlan(cart) {
    const instances = new Map();
    const isOfferActive = window.GroundingKidsCommerceEnabled === true;

    for (const item of cart.items || []) {
      const properties = groundingProperties(item);
      if (!properties) continue;
      const instanceId = properties._landing_offer_instance;
      const instance = instances.get(instanceId) || {
        id: instanceId,
        pack: undefined,
        kids: 0,
        adults: 0,
        gifts: [],
        invalid: false,
      };
      instances.set(instanceId, instance);

      const pack = properties._landing_offer_pack;
      if (!pack || (instance.pack !== undefined && instance.pack !== pack)) {
        instance.invalid = true;
      } else {
        instance.pack = pack;
      }

      if (properties._landing_offer_role === 'qualifying') {
        const quantity = Number(item.quantity) || 0;
        if (quantity <= 0 || properties._landing_offer_gift || properties._landing_offer_gift_key) {
          instance.invalid = true;
        } else if (Number(item.product_id) === GROUNDING_KIDS_PRODUCT_ID) {
          instance.kids += quantity;
        } else if (Number(item.product_id) === GROUNDING_ADULT_PRODUCT_ID) {
          instance.adults += quantity;
        } else {
          instance.invalid = true;
        }
      } else if (properties._landing_offer_role === 'gift') {
        instance.gifts.push({item, properties});
      } else {
        instance.invalid = true;
      }
    }

    const updates = {};
    const additions = [];
    for (const instance of instances.values()) {
      const compositionValid = validGroundingComposition(instance);
      const qualifyingQuantity = instance.kids + instance.adults;
      const seenGiftKeys = new Set();
      for (const {item, properties} of instance.gifts) {
        const key = properties._landing_offer_gift_key;
        const rule = GROUNDING_GIFT_RULES.get(key);
        const valid = isOfferActive &&
          compositionValid &&
          properties._landing_offer_gift === '1' &&
          rule &&
          Number(item.variant_id) === rule.variantId &&
          Number(item.quantity) === 1 &&
          qualifyingQuantity >= rule.unlockQuantity &&
          !seenGiftKeys.has(key);

        if (valid) seenGiftKeys.add(key);
        else updates[item.key] = 0;
      }

      // Gifts are restored only for a complete, backend-recognized pack. A
      // malformed or downgraded pack keeps its paid hoodies but loses rewards.
      if (!isOfferActive || !compositionValid) continue;
      for (const [key, rule] of GROUNDING_GIFT_RULES) {
        if (rule.unlockQuantity > qualifyingQuantity || seenGiftKeys.has(key)) continue;
        additions.push({
          id: rule.variantId,
          quantity: 1,
          properties: {
            _landing_offer_campaign: GROUNDING_CAMPAIGN_ID,
            _landing_offer_instance: instance.id,
            _landing_offer_pack: instance.pack,
            _landing_offer_line: `gift-${key}-${instance.id}`,
            _landing_offer_role: 'gift',
            _landing_offer_gift: '1',
            _landing_offer_gift_key: key,
          },
        });
      }
    }
    return {updates, additions};
  }

  async function reconcile() {
    if (reconciling) {
      reconciliationPending = true;
      return;
    }
    reconciling = true;
    reconciliationPending = false;
    try {
      const cartResponse = await originalFetch(`${root}cart.js`, {
        headers: {'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest'},
      });
      if (!cartResponse.ok) return;
      // Shopify serves cart JSON as `text/javascript` on some storefronts, so
      // status and parseability—not the MIME label—are the reliable signals.
      const cart = await cartResponse.json();
      const calmPlan = calmReconciliationPlan(cart);
      const groundingPlan = groundingReconciliationPlan(cart);
      const pdpUpdates = window.GroundingOffer?.config
        ? window.GroundingOffer.giftUpdates(window.GroundingOffer.config, cart) : {};
      const productUpdates = window.CloudNineProductOffers?.giftUpdates(cart) || {};
      const sensoryUpdates = window.CloudNineSensoryOffers?.giftUpdates(cart) || {};
      const updates = {...calmPlan.updates, ...groundingPlan.updates, ...pdpUpdates, ...productUpdates, ...sensoryUpdates};
      const additions = [...calmPlan.additions, ...groundingPlan.additions];
      if (!Object.keys(updates).length && !additions.length) return;
      let updatedCart = cart;

      // Remove only no-longer-earned or malformed offer gift lines. Paid
      // merchandise and every unrelated cart line remain untouched.
      if (Object.keys(updates).length) {
        const updateResponse = await originalFetch(`${root}cart/update.js`, {
          method: 'POST',
          headers: {'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest'},
          body: JSON.stringify({updates}),
        });
        if (!updateResponse.ok) return;
        updatedCart = await updateResponse.json();
      }

      // Restore only gifts earned by an existing qualifying instance. This
      // covers cart refreshes and accidental gift removal without creating a
      // new offer instance or trusting browser-supplied entitlement values.
      if (additions.length) {
        const addResponse = await originalFetch(`${root}cart/add.js`, {
          method: 'POST',
          headers: {'Content-Type': 'application/json', 'Accept': 'application/json', 'X-Requested-With': 'XMLHttpRequest'},
          body: JSON.stringify({items: additions}),
        });
        if (!addResponse.ok) return;
        const refreshedResponse = await originalFetch(`${root}cart.js`, {headers: {'Accept': 'application/json'}});
        if (refreshedResponse.ok) updatedCart = await refreshedResponse.json();
      }

      if (window.location.pathname === `${root}cart` || window.location.pathname === '/cart') {
        window.location.reload();
        return;
      }

      // Refresh both Dawn-compatible subscribers and the installed Kaching
      // drawer after reconciliation without assuming either owns the cart.
      if (typeof publish === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
        publish(PUB_SUB_EVENTS.cartUpdate, {source: 'calm-kit-reconciler', cartData: updatedCart});
      }
      document.dispatchEvent(new CustomEvent('cart:refresh', {detail: {source: 'calm-kit-reconciler'}}));
      window.dispatchEvent(new CustomEvent('cart:refresh', {detail: {source: 'calm-kit-reconciler'}}));
      await window.kachingCartApi?.refreshCart?.();
    } catch {
      // Cart reconciliation is a safety enhancement. A network failure must not
      // block ordinary cart controls; the backend Function still refuses gifts
      // that are not unlocked by the current qualifying quantity.
    } finally {
      reconciling = false;
      // Re-read the cart once after any mutation observed during the active run.
      if (reconciliationPending) {
        reconciliationPending = false;
        schedule(0);
      }
    }
  }

  // Observe successful cart mutations without changing their requests or
  // responses. Both scoped plans exit immediately when their campaign metadata
  // is absent, so ordinary carts do not receive extra mutations.
  window.fetch = async (...args) => {
    const response = await originalFetch(...args);
    const requestUrl = typeof args[0] === 'string' ? args[0] : args[0]?.url || '';
    if (/\/cart\/(add|change|update|clear)(\.js)?(?:\?|$)/.test(requestUrl)) schedule();
    return response;
  };

  document.addEventListener('DOMContentLoaded', () => schedule(150));
  window.addEventListener('pageshow', () => schedule(150));
  if (typeof subscribe === 'function' && typeof PUB_SUB_EVENTS !== 'undefined') {
    subscribe(PUB_SUB_EVENTS.cartUpdate, (event) => {
      if (event?.source !== 'calm-kit-reconciler') schedule();
    });
  }
})();
